import React, { useEffect, useState } from 'react';
import { Dialog, Button, TextButton, Field, Input, TYPE, cn } from '../ui';
import { PendingConfirmation, payoffPlansApi } from '../../services/api';
import { usd } from './planPayload';

interface PaymentConfirmPromptProps {
  /** Bump to re-check (e.g. after plans reload). */
  refreshKey?: number;
  onResolved: () => void;
}

/**
 * "Did you make this payment?" — requirement 7.
 *
 * The queue is derived from PLANNED rows with an elapsed dueDate, so it needs
 * no lastSeenAt column and survives dismissed dialogs, missed logins and new
 * devices. Answering "a different amount" changes the card's real balance, so
 * the server flags the plan stale and the next open of the card offers the
 * same reconcile choice as a manual balance edit.
 */
export const PaymentConfirmPrompt: React.FC<PaymentConfirmPromptProps> = ({
  refreshKey,
  onResolved,
}) => {
  const [queue, setQueue] = useState<PendingConfirmation[]>([]);
  const [busy, setBusy] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    payoffPlansApi
      .pendingConfirmations()
      .then(rows => {
        if (!cancelled) setQueue(rows);
      })
      .catch(() => {
        /* non-critical — never block the app on this */
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const current = queue[0];
  if (!current || dismissed) return null;

  const advance = () => {
    setQueue(q => q.slice(1));
    setCustomOpen(false);
    setCustomAmount('');
    onResolved();
  };

  const respond = async (
    status: 'CONFIRMED' | 'SKIPPED',
    actualAmount?: number
  ) => {
    setBusy(true);
    try {
      await payoffPlansApi.confirmPayment(current.id, status, actualAmount);
      advance();
    } catch {
      advance(); // don't trap the user behind a failed write
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Confirm a payment"
      subhead={
        <>
          {queue.length > 1 && `${queue.length} payments need confirming. `}
          This keeps your balances accurate.
        </>
      }
      footer={
        !customOpen && (
          <TextButton onClick={() => setDismissed(true)}>Ask me later</TextButton>
        )
      }
    >
      <div className="border-l-2 border-bad pl-4 mb-7">
        <p className={cn(TYPE.body, 'text-ink-soft')}>
          Did you make the <span className="font-semibold text-ink">{usd(current.amount)}</span>{' '}
          <span className="font-semibold text-ink">{current.plan.creditCard.name}</span> payment due{' '}
          <span className="font-semibold text-ink">
            {current.dueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
          </span>
          ?
        </p>
      </div>

      {customOpen ? (
        <div>
          <Field
            label="How much did you pay?"
            hint="A different amount changes the card's balance, so the rest of the plan will need recalculating."
            htmlFor="confirm-amount"
          >
            <Input
              id="confirm-amount"
              autoFocus
              type="number"
              min={0}
              step="0.01"
              value={customAmount}
              onChange={e => setCustomAmount(e.target.value)}
            />
          </Field>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <Button variant="ghost" onClick={() => setCustomOpen(false)}>Back</Button>
            <Button
              variant="primary"
              disabled={busy || !customAmount}
              onClick={() => respond('CONFIRMED', parseFloat(customAmount))}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Button variant="primary" disabled={busy} onClick={() => respond('CONFIRMED')}>
            Yes, I paid {usd(current.amount)}
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => setCustomOpen(true)}>
            I paid a different amount
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => respond('SKIPPED')}>
            No, I skipped it
          </Button>
        </div>
      )}
    </Dialog>
  );
};
